import { ILog } from "@ihr-radioedit/inferno-core";
import { SUPPORTED_LANGUAGES } from "@inferno/renderer-shared-core";

import type { SupportedLanguage } from "@inferno/renderer-shared-core/src/sites/coast/constants";

export interface TranslatorConfig {
  pageLanguage: string;
  includedLanguages: string;
  autoDisplay: boolean;
  layout?: any;
  autoTranslateTo?: string;
}

export interface TranslatorCallbacks {
  onReady?: () => void;
  onError?: (error: Error) => void;
  onLanguageChange?: (language: SupportedLanguage) => void;
}

const log = ILog.logger("GoogleTranslator");
const getDefaultConfig = (): TranslatorConfig => ({
  pageLanguage: "en",
  includedLanguages: SUPPORTED_LANGUAGES.map(lang => lang.code).join(","),
  autoDisplay: false,
});

export class GoogleTranslateManager {
  private scriptUrl: string;
  private initialized = false;
  private config: TranslatorConfig;
  private callbacks: TranslatorCallbacks;

  constructor(scriptUrl: string, config: Partial<TranslatorConfig> = {}, callbacks: TranslatorCallbacks = {}) {
    this.scriptUrl = scriptUrl;
    this.config = { ...getDefaultConfig(), ...config };
    this.callbacks = callbacks;
  }

  async initialize(): Promise<void> {
    if (this.initialized || typeof window === "undefined") return;

    const existingCookie = document.cookie.match(/googtrans=\/([^;]+)/);

    if (this.config.autoTranslateTo && !existingCookie) {
      const pageLang = this.detectPageLanguage();
      const cookieName = "googtrans";
      const cookieValue = `/${pageLang}/${this.config.autoTranslateTo}`;
      document.cookie = `${cookieName}=${cookieValue}; path=/; domain=${this.getDomain()}; max-age=31536000; SameSite=Lax; Secure`;
      log.debug(`Setting translate cookie: ${cookieValue}`);
    }

    if ((window as any).google?.translate) {
      this.markReady();
      return;
    }

    if (document.querySelector(`script[src^="${this.scriptUrl}"]`)) {
      return new Promise(resolve => (this.callbacks.onReady = resolve));
    }

    await new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `${this.scriptUrl}?cb=googleTranslateElementInit`;
      script.async = true;
      script.defer = true;

      (window as any).googleTranslateElementInit = () => {
        this.markReady();
        resolve();
      };

      script.onerror = () => {
        const error = new Error("Failed to load Google Translate script");
        log.error(error);
        this.callbacks.onError?.(error);
        reject(error);
      };

      document.head.appendChild(script);
    });
  }

  private markReady() {
    this.initialized = true;
    this.callbacks.onReady?.();
  }

  private getDomain(): string {
    const hostname = window.location.hostname;
    const parts = hostname.split(".");
    if (parts.length > 2) {
      return `.${parts.slice(parts.length - 2).join(".")}`;
    }
    return hostname;
  }

  createTranslateElement(containerId: string): void {
    if (!this.initialized || !(window as any).google?.translate) {
      log.warn("Google Translate not initialized");
      return;
    }

    try {
      const actualPageLanguage = this.detectPageLanguage();

      new (window as any).google.translate.TranslateElement(
        {
          pageLanguage: actualPageLanguage,
          includedLanguages: this.config.includedLanguages,
          autoDisplay: this.config.autoDisplay,
          layout: this.config.layout || (window as any).google.translate.TranslateElement.InlineLayout.SIMPLE,
        },
        containerId,
      );
      log.debug("Google Translate element created");
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      log.error("Failed to create Translate element:", error);
      this.callbacks.onError?.(error);
    }
  }

  private detectPageLanguage(): string {
    if (typeof document !== "undefined" && document.documentElement) {
      const htmlLang = document.documentElement.lang;
      if (htmlLang) {
        const langCode = htmlLang.split("-")[0];
        return langCode;
      }
    }
    const fallbackLang = this.config?.pageLanguage || "en";
    return fallbackLang;
  }
}

export const createGoogleTranslator = (
  scriptUrl: string,
  config: Partial<TranslatorConfig> = {},
  callbacks: TranslatorCallbacks = {},
): GoogleTranslateManager => new GoogleTranslateManager(scriptUrl, config, callbacks);
